import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getOwnCreated } from "../api/dashboard.api";
import type { OwnCreatedDataResponse } from "../types/dashboard-data.types";
import type { DashboardQueryState } from "./dashboard-query.types";

export const useOwnCreatedData = (): DashboardQueryState<OwnCreatedDataResponse> => {
  const [data, setData] = useState<OwnCreatedDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { run } = useLatestRequest();
  const dataRef = useRef<OwnCreatedDataResponse | null>(null);
  const sessionRef = useRef(0);

  dataRef.current = data;

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      void forceRefresh;
      sessionRef.current += 1;
      const session = sessionRef.current;

      if (dataRef.current !== null) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const result = await run((signal) => getOwnCreated({ signal }));
        if (session !== sessionRef.current) return;
        if (result === undefined) return;
        setData(result);
        setError(null);
      } catch (requestError) {
        if (session !== sessionRef.current) return;
        setError(getApiErrorMessage(requestError, "Failed to fetch own created"));
      } finally {
        if (session === sessionRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [run],
  );

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
    return () => {
      sessionRef.current += 1;
    };
  }, [fetchData]);

  return { data, isLoading, isRefreshing, error, refetch };
};
